// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Truck from "@insite/mobius/Icons/Truck";
import Calendar from "@insite/mobius/Icons/Calendar";
import AlertTriangleFill from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import AlertCircleLine from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import Billing from "@store/Library/Components/Icons/Billing/BillingLine";
import { TrailerTrackingV2Model } from "@store/Framework/Types/ApiModels";
import getColor from "@insite/mobius/utilities/getColor";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import Button from "@store/MobiusOverrides/Button/Button";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";
import TrailerTrackingV2Table, {
    TrailerSortDirection,
    TrailerSortField,
} from "@store/Library/Components/TrailerTrackingV2/TrailerTrackingV2Table";
import { usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import constants from "@store/constants";
import objectValues from "@store/Framework/Common/Utilities/objectValues";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import { focusStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import useIsFirstRender from "@toolkit/Framework/Common/Hooks/useIsFirstRender";
import siteMessage from "@insite/client-framework/SiteMessage";
import capitalize from "lodash/capitalize";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { useTrailerTrackingV2State } from "@store/Framework/Store/Greif/Pages/TrailerTrackingV2/TrailerTrackingV2Hooks";
import attachToLastWord from "@store/Framework/Common/Utilities/attachToLastWord";

export interface TrailerTrackingV2CardProps {
    data: TrailerTrackingV2Model;
    showRequestPickupButton?: boolean;
    toggleCollapse?: boolean;
    toggleExpand?: boolean;
    sortField?: TrailerSortField;
    sortDirection?: TrailerSortDirection;
    onSortClick?: (field: TrailerSortField) => void;
}

export interface TrailerTrackingV2CardStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    headerLink?: ClickablePresentationProps;
    headerColumns?: InjectableCss;
    heading?: InjectableCss;
    headingLabel?: TypographyPresentationProps;
    headingValue?: TypographyPresentationProps;
    expandLink?: LinkPresentationProps;
    expandLinkText?: TypographyPresentationProps;
    expandIcon?: IconPresentationProps;
    alertWrapper?: InjectableCss;
    alertIcon?: IconPresentationProps;
    alertTitle?: TypographyPresentationProps;
    alertText?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    sectionTitleWrapper?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    trailerInvoicesButton?: ButtonPresentationProps;
    noBillingCards?: InjectableCss;
    billingCards?: InjectableCss;
    billingCard?: InjectableCss;
    billingCardTitleWrapper?: InjectableCss;
    billingCardIcon?: IconPresentationProps;
    billingCardTitle?: TypographyPresentationProps;
    billingCardTooltip?: TooltipPresentationProps;
    billingCardInfos?: InjectableCss;
    billingCardInfo?: InjectableCss;
    billingCardInfoLabel?: TypographyPresentationProps;
    billingCardInfoValue?: TypographyPresentationProps;
}

export const trailerTrackingV2CardStyles: TrailerTrackingV2CardStyles = {
    wrapper: {
        css: css`
            width: 100%;
            border: 1px solid ${getColor("common.borderLight")};
            border-radius: ${pxToRem(8)};
            background: ${getColor("common.background")};
            ${x => breakpointValues(x.theme.design.header.height, "scroll-margin-top")}
        `,
    },
    header: {
        css: css`
            position: relative;
            padding: ${pxToRem(16)};
        `,
    },
    headerLink: {
        css: css`
            ${focusStyles()}
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;

            &:hover ~ * {
                &.ship-to-name,
                .ship-to-name {
                    color: ${getColor("primary")};
                }

                &.expand-link,
                .expand-link {
                    color: ${getColor("text.linkHover")};
                    text-decoration: underline;
                }
            }
        `,
    },
    headerColumns: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: space-between;
            gap: ${pxToRem(8)} 8%;

            ${breakpoint("sm")} {
                flex-direction: row;
            }
        `,
    },
    heading: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(8)};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            white-space: nowrap;
            min-width: ${pxToRem(145)};

            &:first-child {
                flex-grow: 1;
                white-space: normal;
                min-width: 0;
            }
        `,
    },
    headingLabel: {
        css: css`
            color: ${getColor("text.light")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            white-space: nowrap;
        `,
    },
    headingValue: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            transition: ${transitionHoverStyles()};
        `,
    },
    expandLink: {
        css: css`
            ${linkActionStyles()}
            display: block;
            width: 100%;
            padding: ${pxToRem(8, 16, 16)};
            align-self: center;

            &.desktop {
                display: none;
            }

            ${breakpoint("lg")} {
                display: none;
                width: auto;
                white-space: nowrap;
                padding: 0;

                &.desktop {
                    display: block;
                }
            }
        `,
        typographyProps: {
            css: css`
                display: grid;
                text-align: center;
                justify-items: center;

                ${breakpoint("lg")} {
                    justify-items: end;
                    text-align: right;
                }
            `,
        },
    },
    expandLinkText: {
        css: css`
            grid-row: 1;
            grid-column: 1;
            display: inline-flex;
            align-items: center;
            gap: ${pxToRem(6)};

            &[aria-hidden="true"] {
                visibility: hidden;
            }
        `,
    },
    expandIcon: {
        size: 18,
        color: "inherit",
    },
    alertWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: ${pxToRem(4, 8)};
            padding: ${pxToRem(7, 12)};
            margin-top: ${pxToRem(12)};
            border-radius: ${pxToRem(4)};
            background-color: ${getColor("warning.lightest")};
            color: ${getColor("text")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    alertIcon: {
        src: AlertTriangleFill,
        size: 18,
        css: css`
            color: ${getColor("warning")};
        `,
    },
    alertTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    alertText: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex-basis: 100%;
            align-items: center;
            gap: inherit;

            span {
                display: flex;
                flex-wrap: wrap;
                align-items: inherit;
                gap: inherit;

                &:not(:last-child)::after {
                    content: "";
                    display: block;
                    width: ${pxToRem(4)};
                    height: ${pxToRem(4)};
                    background-color: currentColor;
                    border-radius: 50%;
                }
            }

            ${breakpoint("sm")} {
                flex-basis: auto;
            }
        `,
    },
    contentWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.borderLight")};
            padding: ${pxToRem(22, 16, 16)};
        `,
    },
    sectionWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(24))}
        `,
    },
    sectionTitleWrapper: {
        css: css`
            ${spacingStyles(null, pxToRem(16))}
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: ${pxToRem(16)};

            ${breakpoint("sm")} {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
            }
        `,
    },
    sectionTitle: {
        css: css`
            font-size: ${pxToRem(20)};
            line-height: 1.2;
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    trailerInvoicesButton: { variant: "tertiary" },
    noBillingCards: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: ${pxToRem(70)};
            padding: ${pxToRem(12)};
            background-color: ${getColor("common.accent")};
            border-radius: ${pxToRem(4)};
            text-align: center;
        `,
    },
    billingCards: {
        css: css`
            display: grid;
            grid-template-columns: 1;
            align-items: stretch;
            gap: ${pxToRem(16)};

            ${breakpoint("sm")} {
                grid-template-columns: repeat(2, minmax(auto, ${pxToRem(360)}));
            }

            ${breakpoint("lg")} {
                grid-template-columns: repeat(3, minmax(auto, ${pxToRem(360)}));
            }
        `,
    },
    billingCard: {
        css: css`
            flex-grow: 1;
            padding: ${pxToRem(12)};
            background-color: ${getColor("common.accent")};
            border-radius: ${pxToRem(4)};
            color: ${getColor("text.light")};
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    billingCardTitleWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: ${pxToRem(7)};
            margin-bottom: ${pxToRem(16)};
            color: ${getColor("text")};
        `,
    },
    billingCardIcon: {
        color: "inherit",
        size: 18,
    },
    billingCardTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    billingCardTooltip: {
        iconProps: {
            color: "text.light",
            size: 18,
            src: AlertCircleLine,
        },
    },
    billingCardInfos: {
        css: css`
            display: flex;
            gap: ${pxToRem(16)};
        `,
    },
    billingCardInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(9)};
        `,
    },
    billingCardInfoLabel: {},
    billingCardInfoValue: {
        css: css`
            span {
                color: ${getColor("text")};
                white-space: nowrap;
            }
        `,
    },
};

const styles = trailerTrackingV2CardStyles;

const TrailerTrackingV2Card = ({
    data: {
        billing,
        shipToAddress,
        shipToName,
        trailers,
        trailersCount,
        complimentaryLimitAlerts,
        shipToCustomerSequence,
    },
    showRequestPickupButton,
    toggleCollapse,
    toggleExpand,
    sortField,
    sortDirection,
    onSortClick,
}: TrailerTrackingV2CardProps) => {
    const [expanded, setExpanded] = useState(false);
    const invoiceHistoryPageLink = usePageLinkByPageType(constants.pageType.invoiceHistory);
    const history = useHistory();
    const isFirstRender = useIsFirstRender();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { hasFilters } = useTrailerTrackingV2State();

    const contractPoNumbers = useMemo(() => {
        return objectValues(billing)
            ?.flat(1)
            .reduce<string[]>((result, x) => {
                return x?.contractPoNumber ? [...result, x.contractPoNumber] : result;
            }, []);
    }, [billing]);

    const invoicesUrl = useMemo(() => {
        if (!invoiceHistoryPageLink?.url || !contractPoNumbers?.length) {
            return undefined;
        }

        return `${invoiceHistoryPageLink.url}?poNumber=${encodeURIComponent(contractPoNumbers.join(","))}`;
    }, [invoiceHistoryPageLink?.url, contractPoNumbers]);

    useEffect(() => {
        if (!isFirstRender && expanded) {
            setExpanded(false);
        }
    }, [toggleCollapse]);

    useEffect(() => {
        if (!isFirstRender && !expanded) {
            setExpanded(true);
        }
    }, [toggleExpand]);

    const ExpandLink = ({ className, onClick }: { className?: string; onClick?: LinkProps["onClick"] }) => {
        return (
            <Link {...styles.expandLink} className={`expand-link${className ? ` ${className}` : ""}`} onClick={onClick}>
                <Typography {...styles.expandLinkText} aria-hidden={expanded}>
                    <span>{translate("Show Details")}</span>
                    <Icon {...styles.expandIcon} src={ChevronDown} />
                </Typography>
                <Typography {...styles.expandLinkText} aria-hidden={!expanded}>
                    <span>{translate("Hide Details")}</span>
                    <Icon {...styles.expandIcon} src={ChevronUp} />
                </Typography>
            </Link>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper} ref={wrapperRef}>
            <StyledWrapper {...styles.header}>
                <Clickable {...styles.headerLink} onClick={() => setExpanded(x => !x)}>
                    <VisuallyHidden>{expanded ? translate("Hide Details") : translate("Show Details")}</VisuallyHidden>
                </Clickable>
                <StyledWrapper {...styles.headerColumns}>
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headingLabel}>{translate("Ship To")}</Typography>
                        <Typography {...styles.headingValue} className="ship-to-name">
                            {shipToName}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.heading}>
                        <Typography {...styles.headingLabel}>{translate("Trailers total")}</Typography>
                        <Typography {...styles.headingValue}>
                            {hasFilters
                                ? translate("{0} of {1}", `${trailers.length}`, `${trailersCount}`)
                                : trailersCount}{" "}
                            {translate(trailersCount === 1 ? "trailer" : "trailers")}
                        </Typography>
                    </StyledWrapper>
                    <ExpandLink className="desktop" />
                </StyledWrapper>
                {Boolean(complimentaryLimitAlerts?.length) && (
                    <StyledWrapper {...styles.alertWrapper}>
                        <Icon {...styles.alertIcon} aria-hidden="true" />
                        <Typography {...styles.alertTitle}>
                            {translate("{0}:", translate("Exceeded complimentary limits"))}
                        </Typography>
                        <Typography {...styles.alertText}>
                            {complimentaryLimitAlerts?.map(x => <span key={x.code}>{x.description}</span>)}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {expanded ? (
                <StyledWrapper {...styles.contentWrapper}>
                    {Boolean(
                        billing?.byDwellTime?.length || billing?.byQuantity?.length || billing?.byFixed?.length,
                    ) && (
                        <StyledWrapper {...styles.sectionWrapper}>
                            <StyledWrapper {...styles.sectionTitleWrapper}>
                                <Typography {...styles.sectionTitle}>
                                    {translate("DropTrailer_Billing_Information")}
                                </Typography>
                                <Button
                                    {...styles.trailerInvoicesButton}
                                    disabled={!invoicesUrl}
                                    onClick={() => invoicesUrl && history.push(invoicesUrl)}
                                >
                                    {translate("View Trailer Invoices")}
                                </Button>
                            </StyledWrapper>
                            <StyledWrapper {...styles.billingCards}>
                                {billing?.byDwellTime?.map(({ contractPoNumber, displayCost, complimentaryAmount }) => (
                                    <StyledWrapper
                                        {...styles.billingCard}
                                        key={`dwellTimeBilling${complimentaryAmount}${contractPoNumber}`}
                                    >
                                        <StyledWrapper {...styles.billingCardTitleWrapper}>
                                            <Icon {...styles.billingCardIcon} aria-hidden="true" src={Calendar} />
                                            <Typography {...styles.billingCardTitle}>
                                                {attachToLastWord(
                                                    translate("Bill by dwell time"),
                                                    <Tooltip
                                                        {...styles.billingCardTooltip}
                                                        text={siteMessage(
                                                            "DropTrailer_Billing_DwellTime_Explanation_ToolTip",
                                                        ).toString()}
                                                    />,
                                                )}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.billingCardInfos}>
                                            <StyledWrapper {...styles.billingCardInfo}>
                                                <Typography {...styles.billingCardInfoLabel}>
                                                    {attachToLastWord(
                                                        translate("Complimentary days"),
                                                        <Tooltip
                                                            {...styles.billingCardTooltip}
                                                            text={siteMessage(
                                                                "DropTrailer_Billing_DwellTime_ComplimentaryAmount_Explanation_ToolTip",
                                                            ).toString()}
                                                        />,
                                                    )}
                                                </Typography>
                                                <Typography {...styles.billingCardInfoValue}>
                                                    <span>{`${complimentaryAmount} ${translate("Days")}`}</span>{" "}
                                                    {translate("per trailer")}
                                                </Typography>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.billingCardInfo}>
                                                <Typography {...styles.billingCardInfoLabel}>
                                                    {attachToLastWord(
                                                        translate("Cost per day"),
                                                        <Tooltip
                                                            {...styles.billingCardTooltip}
                                                            text={siteMessage(
                                                                "DropTrailer_Billing_DwellTime_Cost_Explanation_ToolTip",
                                                            ).toString()}
                                                        />,
                                                    )}
                                                </Typography>
                                                <Typography {...styles.billingCardInfoValue}>
                                                    <span>{displayCost}</span> {translate("per trailer")}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                                {billing?.byQuantity?.map(({ contractPoNumber, displayCost, complimentaryAmount }) => (
                                    <StyledWrapper
                                        key={`quantityBilling${complimentaryAmount}${contractPoNumber}`}
                                        {...styles.billingCard}
                                    >
                                        <StyledWrapper {...styles.billingCardTitleWrapper}>
                                            <Icon {...styles.billingCardIcon} aria-hidden="true" src={Truck} />
                                            <Typography {...styles.billingCardTitle}>
                                                {attachToLastWord(
                                                    translate("Bill by quantity"),
                                                    <Tooltip
                                                        {...styles.billingCardTooltip}
                                                        text={siteMessage(
                                                            "DropTrailer_Billing_Quantity_Explanation_ToolTip",
                                                        ).toString()}
                                                    />,
                                                )}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.billingCardInfos}>
                                            <StyledWrapper {...styles.billingCardInfo}>
                                                <Typography {...styles.billingCardInfoLabel}>
                                                    {attachToLastWord(
                                                        translate("Complimentary amount"),
                                                        <Tooltip
                                                            {...styles.billingCardTooltip}
                                                            text={siteMessage(
                                                                "DropTrailer_Billing_Quantity_ComplimentaryAmount_Explanation_ToolTip",
                                                            ).toString()}
                                                        />,
                                                    )}
                                                </Typography>
                                                <Typography {...styles.billingCardInfoValue}>
                                                    <span>{`${complimentaryAmount} ${translate("trailers")}`}</span>
                                                </Typography>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.billingCardInfo}>
                                                <Typography {...styles.billingCardInfoLabel}>
                                                    {attachToLastWord(
                                                        translate("Cost per month"),
                                                        <Tooltip
                                                            {...styles.billingCardTooltip}
                                                            text={siteMessage(
                                                                "DropTrailer_Billing_Quantity_Cost_Explanation_ToolTip",
                                                            ).toString()}
                                                        />,
                                                    )}
                                                </Typography>
                                                <Typography {...styles.billingCardInfoValue}>
                                                    <span>{displayCost}</span> {translate("per trailer")}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                                {billing?.byFixed?.map(({ contractPoNumber, displayCost, complimentaryAmount }) => (
                                    <StyledWrapper
                                        {...styles.billingCard}
                                        key={`fixedBilling${complimentaryAmount}${contractPoNumber}`}
                                    >
                                        <StyledWrapper {...styles.billingCardTitleWrapper}>
                                            <Icon {...styles.billingCardIcon} aria-hidden="true" src={Billing} />
                                            <Typography {...styles.billingCardTitle}>
                                                {attachToLastWord(
                                                    translate("Bill by fixed"),
                                                    <Tooltip
                                                        {...styles.billingCardTooltip}
                                                        text={siteMessage(
                                                            "DropTrailer_Billing_Fixed_Explanation_ToolTip",
                                                        ).toString()}
                                                    />,
                                                )}
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.billingCardInfos}>
                                            <StyledWrapper {...styles.billingCardInfo}>
                                                <Typography {...styles.billingCardInfoLabel}>
                                                    {translate("Cost per month")}
                                                </Typography>
                                                <Typography {...styles.billingCardInfoValue}>
                                                    <span>{displayCost}</span> {translate("per trailer")}
                                                </Typography>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.sectionWrapper}>
                        <StyledWrapper {...styles.sectionTitleWrapper}>
                            <Typography {...styles.sectionTitle}>{capitalize(translate("trailers"))}</Typography>
                        </StyledWrapper>
                        <TrailerTrackingV2Table
                            trailers={trailers}
                            shipToAddress={shipToAddress}
                            shipToNumber={shipToCustomerSequence}
                            showRequestPickupButton={showRequestPickupButton}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSortClick={onSortClick}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}
            <ExpandLink
                onClick={() => {
                    setExpanded(!expanded);

                    if (expanded) {
                        wrapperRef.current?.scrollIntoView();
                    }
                }}
            />
        </StyledWrapper>
    );
};

export default TrailerTrackingV2Card;
